// Archivos del chat en el navegador: qué se acepta, cómo se comprimen las fotos y cómo se
// suben directo al almacén (Cloudflare) con el permiso que da el API.

export const MAXIMO_ARCHIVOS_POR_ENVIO = 10;
// Una foto de celular pesa 3-4 MB; a este tamaño se ve bien en pantalla y pesa ~300 KB.
const LADO_MAXIMO_FOTO = 1600;
const CALIDAD_FOTO = 0.82;
const COMPRIMIR_DESDE_BYTES = 300 * 1024;

export const extensionDe = (nombre) => {
  const partes = String(nombre || "").toLowerCase().split(".");
  return partes.length > 1 ? partes.pop() : "";
};

export const categoriaDe = (tipo) => {
  const valor = String(tipo || "");
  if (valor.startsWith("image/")) return "imagen";
  if (valor.startsWith("audio/")) return "audio";
  if (valor.startsWith("video/")) return "video";
  return "documento";
};

/** "850 B", "312 KB", "4.2 MB" */
export const formatoTamano = (bytes) => {
  const valor = Number(bytes) || 0;
  if (valor < 1024) return `${valor} B`;
  if (valor < 1024 * 1024) return `${Math.round(valor / 1024)} KB`;
  return `${(valor / 1024 / 1024).toFixed(1)} MB`;
};

/** "0:07", "12:30" */
export const formatoDuracion = (segundos) => {
  const total = Math.max(0, Math.round(Number(segundos) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

/**
 * Por qué no se puede enviar ese archivo, o null si se puede. Las fotos se comprimen antes
 * de subir, así que su tamaño se revisa después (lo revisa el API).
 */
export const problemaDelArchivo = (archivo, { extensiones = [], maximoBytesArchivo = 0 } = {}) => {
  if (extensiones.length && !extensiones.includes(extensionDe(archivo.name))) {
    return `«${archivo.name}» no se puede enviar: ese tipo de archivo no está permitido.`;
  }
  if (!archivo.size) return `«${archivo.name}» está vacío.`;
  const comprimible = /^image\/(jpeg|png|webp)$/.test(archivo.type);
  if (maximoBytesArchivo && archivo.size > maximoBytesArchivo && !comprimible) {
    return `«${archivo.name}» pasa de ${Math.round(maximoBytesArchivo / 1024 / 1024)} MB.`;
  }
  return null;
};

const conExtension = (nombre, extension) => `${String(nombre).replace(/\.[^.]+$/, "") || "foto"}.${extension}`;

/**
 * La foto, más liviana. Si no se puede (GIF animado, HEIC que el navegador no lee) o no
 * gana nada, devuelve la original. Las PNG (capturas de pantalla, con texto) se quedan en
 * PNG; las demás pasan a JPEG.
 */
export const comprimirImagen = async (archivo) => {
  if (!/^image\/(jpeg|png|webp)$/.test(archivo.type) || archivo.size < COMPRIMIR_DESDE_BYTES) return archivo;
  if (typeof createImageBitmap !== "function" || typeof document === "undefined") return archivo;
  try {
    const imagen = await createImageBitmap(archivo);
    const escala = Math.min(1, LADO_MAXIMO_FOTO / Math.max(imagen.width, imagen.height));
    const lienzo = document.createElement("canvas");
    lienzo.width = Math.round(imagen.width * escala);
    lienzo.height = Math.round(imagen.height * escala);
    lienzo.getContext("2d").drawImage(imagen, 0, 0, lienzo.width, lienzo.height);
    imagen.close?.();
    const tipo = archivo.type === "image/png" ? "image/png" : "image/jpeg";
    const blob = await new Promise((resolve) => lienzo.toBlob(resolve, tipo, CALIDAD_FOTO));
    if (!blob || blob.size >= archivo.size) return archivo;
    return new File([blob], conExtension(archivo.name, tipo === "image/png" ? "png" : "jpg"), { type: tipo, lastModified: Date.now() });
  } catch (error) {
    return archivo;
  }
};

/**
 * Sube el archivo al almacén con el permiso del API. `alProgreso` recibe de 0 a 1.
 * Con XMLHttpRequest porque fetch no informa cuánto se ha subido.
 */
export const subirArchivo = (url, archivo, { tipo, alProgreso } = {}) => new Promise((resolve, reject) => {
  const peticion = new XMLHttpRequest();
  peticion.open("PUT", url);
  peticion.setRequestHeader("Content-Type", tipo || archivo.type || "application/octet-stream");
  peticion.upload.onprogress = (evento) => {
    if (evento.lengthComputable) alProgreso?.(evento.loaded / evento.total);
  };
  peticion.onload = () => {
    if (peticion.status >= 200 && peticion.status < 300) {
      resolve();
      return;
    }
    let mensaje = "No se pudo subir el archivo";
    try {
      mensaje = JSON.parse(peticion.responseText).error || mensaje;
    } catch (error) {
      // Sin cuerpo legible: queda el mensaje general.
    }
    reject(new Error(mensaje));
  };
  peticion.onerror = () => reject(new Error("No se pudo subir el archivo. Revisa tu conexión."));
  peticion.send(archivo);
});
